import { useState } from 'react';

import { Box, Container, Typography, useMediaQuery, useTheme, Chip } from '@mui/material';
import dynamic from 'next/dynamic';

import {
	Maybe,
	ImageAsset,
	MultiCardV2,
	MultiCardV2ButtonsCollection,
	MultiCardV2CardsCollection,
	MultiCardV2DescriptionRichText,
	ContentTypeRichTextContent as TContentTypeRichTextContent,
} from '@/types/generated';
import getHeadingVariant from '@/utils/resolveContentfulHeadingVariant';
import { ContentfulBackground } from '@/types';
import { textToJumpLink, resolveContentfulBgColor, translateTagColorNameForContentful } from '@/utils';
import ContentTypeRichText from '@/components/ContentTypeRichText';
import ContentfulButton from '@/components/ContentfulButton';
import TemplateContainer from '@/components/TemplateContainer';
import { IImageAsset } from '@/components/ImageAsset/ImageAsset';

import {
	getSectionWrapperStyles,
	getCardsWrapperStyles,
	wrapper,
	getHeadingStyles,
	richTextDescStyles,
	getMultiCardButtonsStyles,
	backgroundContainerStyles,
	multiCardChipStyles,
	getEyebrowStyles,
} from './MultiCard.styles';
import CardItem from './CardItem';
import { LIMIT_OF_MULTI_CARD_IN_ROW_FOR_DESKTOP, collectUniqueTags, filterCardsByTag } from './MultiCard.helpers';

const CardCarousel = dynamic(() => import('./CardCarousel/CardCarousel'), { ssr: false });
export interface IMultiCard extends MultiCardV2 {
	/**
	 * Used to identify a component on a page for the purpose of jumping to it using the anchor link.
	 *
	 * @type {Maybe<string>}
	 * @memberof IMultiCard
	 */
	anchorId?: Maybe<string>;
	/**
	 * Specifies the background color of the template.
	 *
	 * @type {Maybe<TAGBackground>}
	 * @memberof IMultiCard
	 */
	backgroundColor?: Maybe<string>;
	/**
	 * Specifies if the background image should be displayed on big screens.
	 *
	 * @type {Maybe<boolean>}
	 * @memberof IMultiCard
	 * @deprecated not used in MultiCardV2
	 */
	bigScreenBackground?: Maybe<boolean>;
	/**
	 * Specifies the text alignment of the cards.
	 *
	 * @type {Maybe<flex-start | center>}
	 * @memberof IMultiCard
	 */
	cardsAlignment?: Maybe<string>;
	/**
	 * Cards to be rendered.
	 *
	 * @type {Maybe<MultiCardV2CardsCollection>}
	 * @memberof IMultiCard
	 */
	cardsCollection?: Maybe<MultiCardV2CardsCollection>;
	/**
	 * Template section rich text description
	 *
	 * @type {Maybe<MultiCardV2DescriptionRichText>}
	 * @memberof IMultiCard
	 */
	descriptionRichText?: Maybe<MultiCardV2DescriptionRichText>;
	/**
	 * lastRowCardsAlignment will be applied for more than 3 cards and in desktop mode.
	 *
	 * @type {Maybe<string>}
	 * @memberof IMultiCard
	 * @deprecated not used in MultiCardV2
	 */
	lastRowCardsAlignment?: Maybe<string>;
	/**
	 * Specifies the number of cards in a row on desktop.
	 *
	 * @type {Maybe<number>}
	 * @memberof IMultiCard
	 */
	numberOfCardsInLineDesktop?: Maybe<number>;
	/**
	 * Specifies the section eyebrow text.
	 *
	 * @type {Maybe<string>}
	 * @memberof IMultiCard
	 */
	sectionEyebrow?: Maybe<string>;
	/**
	 * Specifies the text alignment for the section info.
	 *
	 * @type {Maybe<flex-start | center>}
	 * @memberof IMultiCard
	 */
	sectionInfoAlignment?: Maybe<string>;
	/**
	 * Specifies the section title text.
	 *
	 * @type {Maybe<string>}
	 * @memberof IMultiCard
	 */
	sectionTitle?: Maybe<string>;
	/**
	 * Optional setting for choosing the heading type.
	 *
	 * @type {Maybe<HeadingTag>}
	 * @memberof IMultiCard
	 */
	//sectionTitleHtag?: Maybe<HeadingTag>;
	/**
	 * Optional data-testid attribute for testing purposes.
	 *
	 * @type {string}
	 * @memberof IMultiCard
	 */
	dataTestId?: string;
	/**
	 * The collection of buttons to display at the bottom of component.
	 *
	 * @type {Maybe<MultiCardV2ButtonsCollection>}
	 * @memberof IMultiCard
	 */
	buttonsCollection?: Maybe<MultiCardV2ButtonsCollection>;
	/**
	 * The image to display. The reference to an image asset in Contentful.
	 * @type {Maybe<ImageAsset>}
	 * @memberof IMultiCard
	 */
	backgroundImage?: Maybe<ImageAsset>;
	/**
	 * An option to display cards in carousel.
	 *
	 * @type {Maybe<Boolean>}
	 * @memberof IMultiCard
	 */
	shouldDisplayCarouselOnDesktop?: Maybe<boolean>;
	/**
	 * Specifies the layout used on small screens. Can be 'carousel' or 'stack'.
	 *
	 * @type {Maybe<'carousel' | 'stack'>}
	 * @memberof IMultiCard
	 */
	forMobile?: Maybe<string>;
	/**
	 * Specifies the variant of which componenet we want.
	 *
	 * @type {Maybe<string>}
	 * @memberof IMultiCard
	 */
	variant?: Maybe<string>;
}

export default function MultiCard({
	anchorId,
	dataTestId = 'section_multi_card',
	backgroundColor,
	sectionEyebrow,
	sectionTitle,
	descriptionRichText,
	sectionInfoAlignment,
	cardsCollection,
	shouldDisplayCarouselOnDesktop = false,
	cardsAlignment,
	numberOfCardsInLineDesktop,
	buttonsCollection,
	backgroundImage,
	fixBackgroundToBottom = false,
	forMobile = 'stack',
	variant = 'default',
}: Readonly<IMultiCard>) {
	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down('md')); // < 900px

	// solve issue with variant naming structure ie. lightGradient vs Light Gradient
	const contentfulSafeBackgroundColor = translateTagColorNameForContentful(backgroundColor || '') || '';
	const contentfulBackgroundColor = resolveContentfulBgColor(
		contentfulSafeBackgroundColor as ContentfulBackground,
		theme
	);
	const isDarkBackground = !!backgroundColor?.toLowerCase().includes('dark');

	const shouldDisplayCarouselOnMobile = forMobile === 'carousel';
	const cards = cardsCollection?.items;
	const bottomButtons = buttonsCollection?.items;
	const tags = collectUniqueTags(cards);
	const [selectedTag, setSelectedTag] = useState<string | null>(null);
	const filteredCards = filterCardsByTag(cards, selectedTag);
	const maxNumberOfCardsInARowInDesktop =
		filteredCards?.length && filteredCards?.length > LIMIT_OF_MULTI_CARD_IN_ROW_FOR_DESKTOP
			? LIMIT_OF_MULTI_CARD_IN_ROW_FOR_DESKTOP
			: filteredCards?.length ?? LIMIT_OF_MULTI_CARD_IN_ROW_FOR_DESKTOP;

	const onChipClick = (tagName: string, selectedTagName: string | null) => {
		if (selectedTagName && tagName === selectedTagName) {
			setSelectedTag(null);
		} else {
			setSelectedTag(tagName);
		}
	};

	return (
		<TemplateContainer
			maxWidth={false}
			containerSx={{ ...wrapper }}
			dataTestId={dataTestId}
			contentfulBackgroundColor={contentfulBackgroundColor}
			{...(anchorId && { id: textToJumpLink(anchorId) })}
		>
			<Box
				sx={{
					...backgroundContainerStyles(backgroundImage as IImageAsset, fixBackgroundToBottom, isSmallScreen),
				}}
			>
				<Container sx={getSectionWrapperStyles(isSmallScreen, sectionInfoAlignment)}>
					{sectionEyebrow && (
						<Typography
							sx={getEyebrowStyles(theme)}
							variant={isSmallScreen ? 'bodySmallBook' : 'bodyMediumBook'}
							color={isDarkBackground ? 'text.light' : 'text.secondary'}
							data-testid="text_multicard_section_eyebrow"
						>
							{sectionEyebrow}
						</Typography>
					)}

					{sectionTitle && (
						<Typography
							variant={getHeadingVariant('h1')}
							component={'h1'}
							sx={{
								...getHeadingStyles(theme),
								color: isDarkBackground ? 'text.light' : 'text.primary',
							}}
							whiteSpace="pre-line"
							data-testid="text_multicard_section_title"
						>
							{sectionTitle}
						</Typography>
					)}

					{descriptionRichText ? (
						<ContentTypeRichText
							content={descriptionRichText as TContentTypeRichTextContent}
							dataTestId={`text_multicard_section_description_rich_text`}
							disableGutters
							backgroundColor="transparent"
							disablePaddingOn={['Top', 'Bottom']}
							contentMaxWidth="100%"
							docProps={{
								color: isDarkBackground ? 'text.light' : 'text.secondary',
								variant: isSmallScreen ? 'bodyMediumBook' : 'bodyLargeBook',
								textAlign: !isSmallScreen ? 'center' : 'left',
								fontWeight: 350,
							}}
							containerSx={richTextDescStyles(isSmallScreen)}
						/>
					) : null}
					{!!tags.length && (
						<Box display="flex" gap="1rem" data-testid="mulricard_section_tags">
							{tags.map((tag) => (
								<Chip
									key={tag}
									variant={selectedTag === tag ? 'selected' : 'input'}
									sx={multiCardChipStyles}
									onClick={() => onChipClick(tag, selectedTag)}
									clickable
									label={tag}
									data-testid={`mulricard_section_tag_${tag}`}
								/>
							))}
						</Box>
					)}
				</Container>

				<Container
					disableGutters
					sx={getCardsWrapperStyles({
						isDarkBackground,
						cardsAlignment,
						numberOfCards: numberOfCardsInLineDesktop || maxNumberOfCardsInARowInDesktop,
						shouldDisplayCarouselOnDesktop,
					})}
					data-testid="group_multicards_cards"
				>
					{(isSmallScreen && shouldDisplayCarouselOnMobile) || shouldDisplayCarouselOnDesktop ? (
						<CardCarousel
							cards={filteredCards}
							isDarkBackground={isDarkBackground}
							displayAsStepper={false}
							enableMobileCarouselNavigation={false}
							enableResourceCardMode={false}
							isSmallScreen={isSmallScreen}
							useCardMediaRoundedCorners={false}
							useSmallScreenCarouselDesign={false}
							variant={variant}
						/>
					) : (
						filteredCards?.map((card, index: number) =>
							card ? (
								<CardItem
									key={card.sys.id}
									card={card}
									isSmallScreen={isSmallScreen}
									isDarkBackground={isDarkBackground}
									displayAsStepper={false}
									showLeftConnector={index != 0}
									showRightConnector={index != filteredCards.length - 1}
									enableResourceCardMode={false}
									useMediaRoundedCorners={false}
									variant={variant}
								/>
							) : null
						)
					)}
				</Container>

				{bottomButtons && bottomButtons.length > 0 ? (
					<Box sx={getMultiCardButtonsStyles(isSmallScreen)} data-testid="multicard_buttons_box">
						{bottomButtons.map((button) =>
							button ? (
								<ContentfulButton
									key={button?.sys?.id}
									{...button}
									dataTestId={`multicard_button_${button?.name || ''}`}
									docProps={{
										marginTop: isSmallScreen ? '2.5rem' : 0,
										gap: '0.625rem',
										fontSize: '0.875rem',
										fontWeight: 700,
										lineHeight: '1.25rem',
										letterSpacing: '0.188rem',
										color: '#FFFF',
										backgroundColor: '#002855',
										fontFamily: 'Messina Sans',
									}}
								/>
							) : null
						)}
					</Box>
				) : null}
			</Box>
		</TemplateContainer>
	);
}
